import { Client, ResponseType } from '@microsoft/microsoft-graph-client';

function getGraphClient(accessToken) {
  return Client.init({
    authProvider: (done) => done(null, accessToken),
  });
}

// Phase 1 — fast, no admin consent required beyond User.ReadBasic.All
export async function getAllUsersBasic(accessToken) {
  const client = getGraphClient(accessToken);
  const users = [];

  let response = await client
    .api('/users')
    .select('id,displayName,givenName,surname,jobTitle,department,mail,userPrincipalName,officeLocation')
    .top(999)
    .get();

  users.push(...response.value);

  while (response['@odata.nextLink']) {
    response = await client.api(response['@odata.nextLink']).get();
    users.push(...response.value);
  }

  return users;
}

// Phase 2 — enrichment fields that require User.Read.All
// Returns a map of userId → { businessPhones, mobilePhone, hireDate, manager }
export async function getAllUsersDetails(accessToken) {
  const client = getGraphClient(accessToken);
  const details = new Map();

  const toDetail = (u) => ({
    businessPhones: u.businessPhones || [],
    mobilePhone: u.mobilePhone || null,
    hireDate: u.employeeHireDate || null,
    manager: u.manager || null,
  });

  let response = await client
    .api('/users')
    .select('id,businessPhones,mobilePhone,employeeHireDate')
    .expand('manager($select=id,displayName)')
    .top(999)
    .get();

  response.value.forEach((u) => details.set(u.id, toDetail(u)));

  while (response['@odata.nextLink']) {
    response = await client.api(response['@odata.nextLink']).get();
    response.value.forEach((u) => details.set(u.id, toDetail(u)));
  }

  return details;
}

// Keep for backwards compatibility
export async function getAllUsers(accessToken) {
  return getAllUsersBasic(accessToken);
}

export async function getCurrentUser(accessToken) {
  const client = getGraphClient(accessToken);
  return await client
    .api('/me')
    .select('id,displayName,givenName,surname,jobTitle,department,mail,userPrincipalName,officeLocation,businessPhones,mobilePhone')
    .get();
}

export async function getUserById(accessToken, userId) {
  const client = getGraphClient(accessToken);
  return await client
    .api(`/users/${userId}`)
    .select('id,displayName,givenName,surname,jobTitle,department,mail,userPrincipalName,officeLocation,businessPhones,mobilePhone')
    .expand('manager($select=id,displayName,jobTitle)')
    .get();
}

export async function getDirectReports(accessToken, userId) {
  const client = getGraphClient(accessToken);
  try {
    const response = await client
      .api(`/users/${userId}/directReports`)
      .select('id,displayName,jobTitle,department,mail')
      .get();
    return response.value;
  } catch {
    return [];
  }
}

// Returns the raw Blob so callers can cache it. Returns null on failure.
// Fetches the 240x240 thumbnail (~15-30KB) instead of the full-res original —
// this is only the fallback for users the backend photoSync hasn't cached yet,
// so keep the per-user transfer small. Falls back to the original size if Graph
// hasn't rendered that thumbnail for the user.
export async function getUserPhoto(accessToken, userId) {
  const client = getGraphClient(accessToken);
  try {
    return await client
      .api(`/users/${userId}/photos/240x240/$value`)
      .responseType(ResponseType.BLOB)
      .get();
  } catch {
    try {
      return await client
        .api(`/users/${userId}/photo/$value`)
        .responseType(ResponseType.BLOB)
        .get();
    } catch {
      return null;
    }
  }
}

export async function getUserPhotos(accessToken, userIds) {
  const CONCURRENCY = 10;
  const results = {};
  for (let i = 0; i < userIds.length; i += CONCURRENCY) {
    const chunk = await Promise.all(
      userIds.slice(i, i + CONCURRENCY).map(async (id) => [id, await getUserPhoto(accessToken, id)])
    );
    chunk.forEach(([id, url]) => { results[id] = url; });
  }
  return results;
}

// Fetches presence for up to 650 users per call
export async function getPresenceByUserIds(accessToken, userIds) {
  const client = getGraphClient(accessToken);
  const presenceMap = {};
  const batchSize = 650;

  for (let i = 0; i < userIds.length; i += batchSize) {
    try {
      const response = await client
        .api('/communications/getPresencesByUserId')
        .post({ ids: userIds.slice(i, i + batchSize) });
      response.value.forEach((p) => {
        presenceMap[p.id] = p.availability;
      });
    } catch (err) {
      if (err?.statusCode === 403) {
        console.warn('PeopleVUE: Presence.Read.All permission not granted — statuses unavailable. An admin must consent to this scope.');
      } else {
        console.warn('Could not fetch presence:', err);
      }
    }
  }

  return presenceMap;
}
